import Intercom, { show, shutdown } from '@intercom/messenger-js-sdk'
import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { logError } from '@/logger'
import { OlympusClient } from '@/OlympusClient'
import { useNotify } from '@/utils/useNotify'

export const useIntercomStore = defineStore('intercom', () => {
  const notify = useNotify()
  const { t } = useI18n()
  const intercomInitialized = ref(false)

  /**
   * Initializes Intercom with a user JWT token.  Throws if token fetch fails.
   */
  const initializeIntercom = async () => {
    if (intercomInitialized.value) return

    try {
      // Eagerly mark as initialized to prevent concurrent calls.  If token fetch fails, it will try
      // again the next time the button is clicked.
      intercomInitialized.value = true

      const response = await OlympusClient.auth.v2.hermes.getIntercomToken({})

      Intercom({
        app_id: import.meta.env.VITE_INTERCOM_APP_ID,
        hide_default_launcher: true,
        intercom_user_jwt: response.token,
      })
    } catch (e) {
      logError(e)
      notify(t('core.errors.intercom-load'), 'error')
      intercomInitialized.value = false
    }
  }

  const shutdownIntercom = () => {
    if (intercomInitialized.value) {
      try {
        shutdown()
      } catch (e) {
        // Silently suppress Intercom shutdown errors (e.g., during logout)
        // No need to notify users about support chat cleanup failures
        logError(e)
      } finally {
        intercomInitialized.value = false
      }
    }
  }

  const showIntercom = async () => {
    if (!intercomInitialized.value) {
      await initializeIntercom()
    }

    if (intercomInitialized.value) {
      show()
    }
  }

  return {
    initializeIntercom,
    intercomInitialized,
    showIntercom,
    shutdownIntercom,
  }
})
